import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TabGroup, Tab } from 'ui/tabs';
import { Table, sortRows } from 'ui/table';
import { Skeleton } from 'ui/skeleton';
import TableHeader from 'ui/tableHeader/TableHeader';
import TableRows from 'ui/tableRows/TableRows';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { find, unionBy, differenceBy } from 'lodash';
import { PaginationFooter } from 'ui/paginationFooter';
import { DESCENDING } from 'constants/sortDirections';
import Pills from 'core/pills';
import {
  selectEntityPills,
  selectReportDataSourceModal,
  getActiveReport,
} from 'modules/report/selectors';
import {
  fetchReportingTypeTable,
  updateReportFilters,
  updateReportingTypeModalIds,
  updateReportingTypeTableFilters,
} from 'modules/report/actions';
import * as t from './actionTypes';
import * as actions from './actions';
import { generateRowColumns, generateHeaderColumns } from './utils';
import {
  DEFAULT_TABLE_CONFIG,
  CAMPAIGN,
  CAMPAIGNS,
  WORKFLOW,
  JOURNEYS,
  DISPLAY_NAMES,
} from './constants';
import { Container, TableHeaderContainer, StyledInputSearch } from './ReportDataSourceTable.styled';

export { t, actions };

// type Props = {
//   resetSelectedCampaignsOnMount? /*: boolean */,
// };

export const ReportDataSourceTable = ({ resetSelectedCampaignsOnMount }) => {
  const dispatch = useDispatch();
  const report = useSelector(getActiveReport);
  const selectedEntityPills = useSelector(selectEntityPills);
  const reportingTypeTable = useSelector(selectReportDataSourceModal);

  const {
    isLoading,
    tableConfig = DEFAULT_TABLE_CONFIG,
    reportingEntities,
    selectedTypeIds,
    selectedTypeIdsInModal,
  } = reportingTypeTable;
  const [entityPills, setEntityPills] = useState(selectedEntityPills);
  const [tracked, track] = useState({});
  const [componentType, setComponentType] = useState(CAMPAIGN);
  const [sortedBy, setSortedBy] = useState('id');
  const [filter, setFilter] = useState('');
  const [sortDirection, setSortDirection] = useState(DESCENDING);

  const sortedRows = sortRows(reportingEntities, sortedBy, sortDirection);

  const componentTypeDisplayText =
    entityPills && (entityPills.length > 1 || entityPills.length === 0)
      ? DISPLAY_NAMES[componentType].displayNamePlural
      : DISPLAY_NAMES[componentType].displayName;
  const selectedEntityPillsText = `You have selected ${entityPills.length} ${componentTypeDisplayText}`;

  const createEntityPill = (id /*: number */) => {
    const { name } = find(reportingEntities, { id });
    return { text: `${id}: ${name}`, value: id };
  };

  // add/remove from entity pills array
  const addOrRemoveEntityPills = (isAdding /*: boolean */, entities /* any[] */) => {
    if (isAdding) {
      setEntityPills(unionBy(entityPills, entities, 'value'));
    } else {
      setEntityPills(entities.length ? differenceBy(entityPills, entities, 'value') : []);
    }
  };

  // switching between "campaign" and "journeys"
  const componentTypeChange = (value) => {
    setComponentType(value);
    dispatch(
      fetchReportingTypeTable(value, {
        tableConfig,
        selectedTypeIds,
      }),
    );
    dispatch(
      updateReportFilters({
        id: report.id,
        componentType: value,
      }),
    );
  };

  const handleCheckboxClick = (id /*: number */, isAdding /*: boolean */) => {
    addOrRemoveEntityPills(isAdding, [createEntityPill(id)]);
    dispatch(updateReportingTypeModalIds(isAdding, id));
  };

  const handleToggleAllEntities = (ids /*: number[] */, isAddingAll /*: boolean */) => {
    const newEntities = ids.map((id) => createEntityPill(id));
    addOrRemoveEntityPills(isAddingAll, newEntities);
    dispatch(actions.updateToggleAllEntities(isAddingAll, ids));
  };

  // clicking on pill 'x'
  const handlePillOnDelete = (pill /*: { value: number, text: string } */) => {
    addOrRemoveEntityPills(false, [pill]);
    dispatch(actions.removeEntity(pill));
  };

  const fetchDataOnPageFooterUpdate = (paginationChanges) => {
    dispatch(
      updateReportingTypeTableFilters({
        ...tableConfig,
        page: paginationChanges.page,
        pageSize: paginationChanges.pageSize,
        sortDesc: sortDirection === DESCENDING,
      }),
    );
  };

  useEffect(() => {
    // modal state is kept in redux, and can therefore carry over
    // unwanted state artifacts when the component is mounted.
    // so we reset the state to the initial values, if desired
    if (resetSelectedCampaignsOnMount) {
      dispatch(
        fetchReportingTypeTable(componentType, {
          ...tableConfig,
          selectedTypeIds: {},
        }),
      );
    }
  }, []);

  useEffect(() => {
    // Need to update the state after the workflow/campaign is fetched and done loading.
    if (isLoading !== tracked.isLoading && selectedEntityPills !== tracked.selectEntityPills) {
      setEntityPills(selectedEntityPills);
    }
    track({ selectedEntityPills, isLoading });
  }, [selectedEntityPills, isLoading]);

  useDebouncedEffect(
    () => {
      // fetch data on filter update
      dispatch(updateReportingTypeTableFilters({ ...tableConfig, filter }));
    },
    [filter],
    { debounceTimeout: 600, skipEffectOnMount: true },
  );

  return (
    <Container>
      <TabGroup selectedTab={componentType} onTabChange={(id) => componentTypeChange(id)}>
        <Tab
          data-test="ReportDataSourceTable-componentType-campaigns"
          id={CAMPAIGN}
          label={CAMPAIGNS}
          squareTheme
        />
        <Tab
          data-test="ReportDataSourceTable-componentType-workflows"
          id={WORKFLOW}
          label={JOURNEYS}
          squareTheme
        />
      </TabGroup>
      {isLoading ? (
        <Skeleton height="450" />
      ) : (
        <>
          <TableHeaderContainer>
            <Pills
              pills={entityPills}
              onDeleteHandler={handlePillOnDelete}
              text={selectedEntityPillsText}
            />
            <StyledInputSearch
              handleClear={() => setFilter('')}
              id="filter-reports"
              onChange={setFilter}
              placeholder={'Filter...'}
              value={filter}
            />
          </TableHeaderContainer>
          <Table data-test="report-data-source-table">
            <TableHeader
              columns={generateHeaderColumns(
                handleToggleAllEntities,
                sortedRows,
                selectedTypeIdsInModal,
                componentType,
              )}
              sortedBy={sortedBy}
              setSortedBy={setSortedBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
            <TableRows
              data-test="report-data-source-table-rows"
              rows={sortedRows}
              columns={generateRowColumns(
                handleCheckboxClick,
                selectedTypeIdsInModal,
                componentType,
              )}
            />
          </Table>
          <PaginationFooter
            onUpdate={fetchDataOnPageFooterUpdate}
            pageSizes={[10, 20, 50]}
            currentPage={tableConfig.page}
            itemsPerPage={tableConfig.pageSize}
            numItems={reportingTypeTable.total}
          />
        </>
      )}
    </Container>
  );
};

export default ReportDataSourceTable;
